'use client'

import * as Sentry from '@sentry/nextjs'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { forwardRef, useCallback } from 'react'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { ISearchResult } from '@/_types/search'
import { sendSearchResultClickEvent } from '@/_utils/analytics.utils'

import {
	getMatchedFieldDescription,
	getMatchedFieldName,
	getModelName,
	getSearchResultUrl
} from '../mobile/_utils/search-result.utils'

interface ADesktopSearchResultsProps {
	results: ISearchResult[]
	isLoading: boolean
	isSearching: boolean
	hasSearchTerm: boolean
	onResultClick: () => void
	searchQuery?: string
}

interface ADesktopSearchResultProps {
	result: ISearchResult
	handleResultMouseDown: (e: React.MouseEvent<HTMLAnchorElement>) => void
	handleResultClick: (e: React.MouseEvent<HTMLAnchorElement>, url: string) => void
}

const ADesktopSearchResult = ({
	result,
	handleResultMouseDown,
	handleResultClick
}: ADesktopSearchResultProps) => {
	try {
		const title = getMatchedFieldName(result)
		const url = getSearchResultUrl(result)
		const description = getMatchedFieldDescription(result) || getModelName(result)

		return (
			<Link
				key={`${result.model}-${result.id}`}
				href={nextLinkHrefFromPathWithOptionalHash(url)}
				className='desktop-header__search-result-item'
				onMouseDown={handleResultMouseDown}
				onClick={e => handleResultClick(e, url)}
			>
				<div
					className='desktop-header__search-result-title'
					dangerouslySetInnerHTML={{ __html: title }}
				/>
				{description && typeof description === 'string' && (
					<div
						className='desktop-header__search-result-description'
						dangerouslySetInnerHTML={{ __html: description }}
					/>
				)}
			</Link>
		)
	} catch (error) {
		Sentry.captureException(error)
		return null
	}
}

const ADesktopSearchResults = forwardRef<HTMLDivElement, ADesktopSearchResultsProps>(
	({ results, isLoading, isSearching, hasSearchTerm, onResultClick, searchQuery = '' }, ref) => {
		const router = useRouter()

		const handleResultClick = useCallback(
			(e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
				e.preventDefault()
				e.stopPropagation()

				// Send GA event for search result click with destination URL
				sendSearchResultClickEvent(searchQuery, url)

				router.push(url)
				onResultClick()
			},
			[router, onResultClick, searchQuery]
		)

		const handleResultMouseDown = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault()
			// Just prevent default, let onClick handle everything including GA
		}, [])

		if (!hasSearchTerm) {
			return null
		}

		if (isSearching || isLoading) {
			return (
				<div ref={ref} className='desktop-header__search-results'>
					<div className='desktop-header__search-results-loading'>Vyhľadávanie...</div>
				</div>
			)
		}

		if (results.length === 0) {
			return (
				<div ref={ref} className='desktop-header__search-results'>
					<div className='desktop-header__search-results-empty'>
						Nenašli sa žiadne výsledky
					</div>
				</div>
			)
		}

		return (
			<div ref={ref} className='desktop-header__search-results'>
				<div className='desktop-header__search-results-list'>
					{results.map(result => {
						return (
							<ADesktopSearchResult
								key={`${result.model}-${result.id}`}
								result={result}
								handleResultMouseDown={handleResultMouseDown}
								handleResultClick={handleResultClick}
							/>
						)
					})}
				</div>
			</div>
		)
	}
)

ADesktopSearchResults.displayName = 'ADesktopSearchResults'

export default ADesktopSearchResults
